import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { getEventByJoinCode, getQuestionsByEvent, getResponses, getLatestClusters, hasGeoStreams } from "../lib/api";
import { isVisibleResponse } from "../lib/responses";
import { useInsights } from "../hooks/useInsights";
import { ManifestoCard } from "./InsightsView/ManifestoCard";
import { TopThemes } from "./InsightsView/TopThemes";
import { ThemeVelocity } from "./InsightsView/ThemeVelocity";
import { StadtteilHeatmap } from "./InsightsView/StadtteilHeatmap";
import { resolveView } from "../lib/stageViews";
import StageResultsPanel from "../components/Stage/StageResultsPanel";
import type { SegmentStats } from "../hooks/useSegmentStats";
import type { Cluster, Question, Response } from "../lib/types";

type QSummary = { question: Question; responses: Response[]; clusters: Cluster[] };

// No live stats fetch on the static insights page — the neutral value keeps
// `compareModel` unavailable, which is fine since `segmentQuestion` below is
// always null (resolveView never selects "compare" without a segment).
const NEUTRAL_STATS: SegmentStats = { respondents: 0, unlinked: 0, segments: [], loading: false, error: false };

/** Published-page equivalent of the stage's default view per question type —
 *  no segment comparison here (no live stats fetch on the static insights
 *  page), just the canonical single-question layout for each type, via the
 *  same StageResultsPanel dispatcher the stage uses (so MCQ "other" / polar
 *  side_a / side_b cohort cluster lists show here too). */
function QuestionResultView({
  question,
  visibleResponses,
  clusters,
}: {
  question: Question;
  visibleResponses: Response[];
  clusters: Cluster[];
}) {
  if (question.question_type === "open_text") return null;
  const view = resolveView(question, { segment_question_id: null });
  const responsesByCluster = new Map<string, Response[]>();
  for (const r of visibleResponses) {
    if (r.cluster_id == null) continue;
    const list = responsesByCluster.get(r.cluster_id) ?? [];
    list.push(r);
    responsesByCluster.set(r.cluster_id, list);
  }
  return (
    <StageResultsPanel
      question={question}
      segmentQuestion={null}
      responses={visibleResponses}
      clusters={clusters}
      responsesByCluster={responsesByCluster}
      stats={NEUTRAL_STATS}
      view={view}
      quoteIndex={0}
    />
  );
}

function useQuestionSummaries(eventId: string | null): QSummary[] {
  const [out, setOut] = useState<QSummary[]>([]);
  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    (async () => {
      const qs = await getQuestionsByEvent(eventId);
      const summaries: QSummary[] = [];
      for (const q of qs) {
        const responses = await getResponses(q.id);
        const clusters = await getLatestClusters(q.id);
        summaries.push({ question: q, responses, clusters });
      }
      if (!cancelled) setOut(summaries);
    })();
    return () => { cancelled = true; };
  }, [eventId]);
  return out;
}

interface Props {
  joinCode: string;
  embed: boolean;
}

// ── OG tag helper ─────────────────────────────────────────────────────────────

function setMeta(property: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export default function InsightsView({ joinCode, embed }: Props) {
  const [eventId, setEventId] = useState<string | null>(null);
  const [resolving, setResolving] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Resolve join code → event id once on mount
  useEffect(() => {
    let cancelled = false;
    getEventByJoinCode(joinCode).then((ev) => {
      if (cancelled) return;
      if (ev) {
        setEventId(ev.id);
      } else {
        setNotFound(true);
      }
      setResolving(false);
    }).catch(() => {
      if (!cancelled) {
        setNotFound(true);
        setResolving(false);
      }
    });
    return () => { cancelled = true; };
  }, [joinCode]);

  const { event, questions, topClusters, manifesto, totalResponses, error } =
    useInsights(eventId);

  const summaries = useQuestionSummaries(eventId);

  // The Stadtteil heatmap only applies to events with geo-tagged streams
  // (civic-dialogue setups). Everyone else gets no map section at all.
  const [showHeatmap, setShowHeatmap] = useState(false);
  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    hasGeoStreams(eventId).then((has) => {
      if (!cancelled) setShowHeatmap(has);
    });
    return () => { cancelled = true; };
  }, [eventId]);

  // Published page shows only questions that gathered visible answers —
  // a question skipped during the event adds nothing to the results.
  const answeredSummaries = summaries
    .map((s) => ({ ...s, visibleResponses: s.responses.filter((r) => isVisibleResponse(r)) }))
    .filter((s) => s.visibleResponses.length > 0);

  // ── Document title + OG meta tags ────────────────────────────────────────
  useEffect(() => {
    if (!event) return;

    const pageTitle = `${event.title} — Insights · REASONZ`;
    document.title = pageTitle;

    // OG:title
    setMeta("og:title", pageTitle);

    // OG:description — first 160 chars of manifesto, or fallback
    const ogDescription = manifesto?.content
      ? manifesto.content.replace(/\n+/g, " ").slice(0, 160)
      : "Lebendige Echtzeit-Insights für Events und Diskussionen";
    setMeta("og:description", ogDescription);

    // OG:image — placeholder; per-event dynamic OG image generation is a v2 feature
    // TODO(og-image): Generate per-event OG images server-side (v2). Track at: https://github.com/readycal/reasonz/issues
    setMeta("og:image", "https://reasonz.de/og-default.png");

    setMeta("og:type", "website");
    setMeta("og:url", window.location.href);
  }, [event, manifesto?.content]);

  // ── Not found (only when join-code resolve definitively failed) ─────────────
  if (notFound) {
    return (
      <div className="min-h-screen bg-rc-cream flex items-center justify-center px-6">
        <div className="text-center max-w-sm">
          <p className="text-[32px] font-bold text-rc-black mb-3">404</p>
          <p className="text-[16px] text-rc-charcoal mb-6">
            Insights nicht gefunden. Prüfe den Event-Code und versuche es erneut.
          </p>
          <a
            href="/"
            className="inline-flex items-center gap-2 bg-rc-berry text-rc-paper rounded-pill px-6 py-3 text-[14px] font-semibold hover:bg-rc-berry-700 transition ease-rc-out"
          >
            ← Zur Startseite
          </a>
        </div>
      </div>
    );
  }

  // ── Loading state — anything before the event is resolved keeps the spinner.
  //   Crucially we wait on `!event` rather than `loading`, because there is a
  //   brief window after the join-code resolves where `loading` has already
  //   flipped to false but the new event-id fetch hasn't started yet.
  if (resolving || !event) {
    return (
      <div
        className="min-h-screen bg-rc-cream flex items-center justify-center"
        role="status"
        aria-label="Lade Insights…"
      >
        <Loader2 className="w-7 h-7 text-rc-berry animate-spin" />
      </div>
    );
  }

  const CONTENT_CLASS = `max-w-7xl mx-auto ${embed ? "px-4 md:px-6" : "px-6 md:px-8"}`;

  return (
    <div className="min-h-screen bg-rc-cream text-rc-black font-sans antialiased selection:bg-rc-lime selection:text-rc-black">

      {/* ── Header (hidden in embed mode) ────────────────────────────────── */}
      {!embed && (
        <header
          className="sticky top-0 z-50 border-b border-rc-fog"
          style={{
            background: "rgba(250, 248, 244, 0.86)",
            backdropFilter: "saturate(140%) blur(10px)",
            WebkitBackdropFilter: "saturate(140%) blur(10px)",
          }}
        >
          <div className="max-w-7xl mx-auto px-6 md:px-8 py-4 flex items-center gap-4 min-w-0">
            <a href="/" aria-label="READYCAL" className="inline-flex items-center flex-shrink-0">
              <img src="/brand/readycal-logo.png" alt="READYCAL" className="h-6 w-auto block" />
            </a>
            <span className="text-rc-fog/60 text-[16px] font-light flex-shrink-0" aria-hidden>
              ·
            </span>
            <span className="text-[13px] font-semibold uppercase tracking-[0.1em] text-rc-berry flex-shrink-0">
              REASONZ
            </span>
            <span className="ml-auto text-[14px] font-medium text-rc-black truncate min-w-0 text-right">
              {event.title}
            </span>
          </div>
        </header>
      )}

      {/* ── Main content ─────────────────────────────────────────────────── */}
      <main>

        {/* ── Hero block — Manifesto ──────────────────────────────────────── */}
        <section className={`${CONTENT_CLASS} py-12 md:py-20 lg:py-28`}>
          <div className="mb-6">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-rc-berry mb-3">
              Insights · {event.title}
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl 2xl:text-6xl font-bold tracking-[-0.02em] text-rc-black mb-2 text-balance">
              Living Manifesto
            </h1>
            {error && (
              <p className="text-[13px] text-rc-berry mt-2">
                Verbindungsfehler — letzte Daten werden angezeigt.
              </p>
            )}
          </div>
          <ManifestoCard insight={manifesto} eventTitle={event.title} />
        </section>

        {/* ── Clusters + Velocity ─────────────────────────────────────────── */}
        <section className={`${CONTENT_CLASS} pb-10 md:pb-14`}>
          <div className="grid md:grid-cols-2 gap-6 md:gap-8 lg:gap-10">
            <TopThemes clusters={topClusters} eventTitle={event.title} />
            <ThemeVelocity eventId={event.id} questionIds={questions.map((q) => q.id)} eventTitle={event.title} />
          </div>
        </section>

        {/* ── Stadtteil Heatmap (geo-stream events only) ──────────────────── */}
        {showHeatmap && (
          <section className={`${CONTENT_CLASS} pb-16 md:pb-20`}>
            <StadtteilHeatmap eventId={event.id} eventTitle={event.title} />
          </section>
        )}

        {/* ── Per-question aggregation summaries ──────────────────────────── */}
        {answeredSummaries.length > 0 && (
          <section className={`${CONTENT_CLASS} pb-16 md:pb-20`}>
            <h2 className="text-2xl font-semibold text-rc-ink mb-6">Ergebnisse je Frage</h2>
            <div className="flex flex-col gap-10">
              {answeredSummaries.map(({ question, clusters, visibleResponses }, i) => (
                <article key={question.id} className="flex flex-col gap-4 rounded-2xl bg-rc-paper p-6 shadow-card">
                  <header>
                    <div className="text-xs uppercase tracking-wide text-rc-ink/50">
                      {/* Sequential position on the page — order_index can have
                          gaps after question deletions and is 1-based already. */}
                      Frage {i + 1} · {visibleResponses.length} Antworten
                    </div>
                    <h3 className="mt-1 text-xl text-rc-ink">{question.text}</h3>
                  </header>
                  {question.question_type === "open_text" ? (
                    clusters.length > 0 ? (
                      <ul className="flex flex-col gap-1">
                        {clusters.slice(0, 5).map((c) => (
                          <li key={c.id} className="text-rc-ink">
                            <span className="font-medium">{c.label}</span>
                            <span className="ml-2 text-rc-ink/60">{c.size}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="flex flex-col gap-1.5">
                        <p className="text-sm text-rc-ink/60">Stimmen aus dem Raum:</p>
                        <ul className="flex flex-col gap-1">
                          {/* Prefer substantive answers as sample voices; emoji-only
                              and one-word entries only if nothing longer exists. */}
                          {[...visibleResponses]
                            .sort((a, b) => Math.min(b.text.length, 40) - Math.min(a.text.length, 40))
                            .slice(0, 5)
                            .map((r) => (
                              <li key={r.id} className="text-rc-ink">„{r.text}“</li>
                            ))}
                        </ul>
                      </div>
                    )
                  ) : (
                    <div className="rounded-xl bg-rc-ink p-5 text-rc-cream">
                      <QuestionResultView
                        question={question}
                        visibleResponses={visibleResponses}
                        clusters={clusters}
                      />
                    </div>
                  )}
                </article>
              ))}
            </div>
          </section>
        )}

      </main>

      {/* ── Footer (hidden in embed mode) ────────────────────────────────── */}
      {!embed && (
        <footer className="border-t border-rc-fog">
          <div className="max-w-7xl mx-auto px-6 md:px-8 py-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
            <div className="flex items-center gap-2">
              <span className="relative inline-flex w-2 h-2">
                <span className="absolute inset-0 rounded-full bg-rc-berry opacity-50 animate-shimmer" />
                <span className="relative inline-flex w-2 h-2 rounded-full bg-rc-berry" />
              </span>
              <span className="text-[12px] text-rc-slate tabular-nums">
                {totalResponses} {totalResponses === 1 ? "Beitrag" : "Beiträge"} · Live
              </span>
            </div>
            <div className="flex gap-5 sm:ml-auto text-[12px] text-rc-slate">
              <a href="/datenschutz" className="hover:text-rc-black transition-colors">
                Datenschutz
              </a>
              <a href="/impressum" className="hover:text-rc-black transition-colors">
                Impressum
              </a>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}

// Dry-run notes (agent self-test, 2026-05-24, seed event PULZ):
//  - Build: PASS (npm run build clean; lint + typecheck clean).
//  - axe-core 4.11.4 (Chrome 148 headless, 5s load-delay) on /insights/PULZ, /insights/PULZ?embed=1, /datenschutz, /impressum: 0 violations.
//  - Mobile (390x844, DPR=2, mobile=true via CDP Emulation): document scrollWidth === viewport (390) — no horizontal overflow. Manifesto, top-themes, velocity chart, heatmap all render cleanly with natural text wrap.
//  - Tablet (820x1180), Desktop (1440x900), 4K (3840x2160): all render correctly; 2-col grid for top themes + velocity chart activates at md:.
//  - Recharts Y-axis label ("Antworten") on the velocity chart renders narrowly on <400px viewports — a recharts native quirk, readable but slightly clipped. Acceptable for v1.
//  - HUMAN HAND-OFF: real Safari iOS / Firefox checks, projector 4K hardware test, screen-reader QA (VoiceOver, NVDA), and the pilot run are not agent-completable.
